import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

export type InteractionType = 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'WHATSAPP';

export interface Interaction {
  id: string;
  tenantId: string;
  customerId: string;
  customerName: string | null;
  contactId: string | null;
  contactName: string | null;
  opportunityId: string | null;
  type: InteractionType;
  occurredAt: string;
  notes: string;
  participants: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface InteractionInput {
  customerId: string;
  contactId?: string;
  opportunityId?: string;
  type: InteractionType;
  occurredAt?: string;
  notes: string;
  participants?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* RF-011/RF-023: histórico do cliente em ordem decrescente. */
export function useInteractions(params?: {
  customerId?: string;
  contactId?: string;
  opportunityId?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<Interaction>>> {
  return useQuery<PaginatedResponse<Interaction>>({
    queryKey: ['crm', 'interactions', params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<Interaction>>('/interactions', {
        params: {
          ...(params?.customerId ? { customerId: params.customerId } : {}),
          ...(params?.contactId ? { contactId: params.contactId } : {}),
          ...(params?.opportunityId ? { opportunityId: params.opportunityId } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

export function useCreateInteraction() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, InteractionInput, unknown>({
    mutationFn: (input: InteractionInput) =>
      apiClient.post<Interaction>('/interactions', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm', 'interactions'] });
    },
  });
}
